/*
  AbilityProvider for React inside Insurer Portal
  Consumes CanCanCan abilities in Casl format for authorization.
*/

import { createMongoAbility, MongoAbility } from "@casl/ability";
import * as React from "react";
import { useSession } from "./session";

export interface AbilityContextValue {
  ability: MongoAbility;
}

export const AbilityContext = React.createContext<AbilityContextValue | null>(
  null,
);

export const useAbility = (): AbilityContextValue => {
  const contextValue = React.useContext<AbilityContextValue | null>(
    AbilityContext,
  );
  if (!contextValue) {
    throw new Error("useAbility must be used within an AbilityProvider");
  }
  return contextValue;
};

export const AbilityProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { session } = useSession();

  // we may want to run this in an effect for session updates...
  // though as this code is nested inside SessionProvider it's likely not required!
  const ability = createMongoAbility(session?.user?.permissions);

  const defaultContextValue: AbilityContextValue = React.useMemo(
    () => ({
      ability: ability,
    }),
    [ability],
  );

  return (
    <AbilityContext.Provider value={defaultContextValue}>
      {children}
    </AbilityContext.Provider>
  );
};
